import throttle from 'lodash.throttle';
import { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Mousewheel } from 'swiper/modules';

import Heading from '@components/ui/Heading';
import CategoryNavItem from '@components/cms/Slices/CategoryNavCarousel/CategoryNavItem';
import { eePromoClickHandler } from '@utils/gtm';

import {
  CategoryNavCarouselItem,
  CategoryNavCarouselSliceData,
  PageType,
} from '@adapters/prismic/types/slices';

import {
  trackPromotionEvent,
  PromotionEventName,
  InteractionLocation,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/mousewheel';

import styles from './CategoryNavCarousel.module.scss';

export interface CategoryNavCarouselProps {
  title: CategoryNavCarouselSliceData['primary']['title'];
  items: CategoryNavCarouselSliceData['items'];
  trackPromos?: boolean;
  pageType: PageType;
  sliceIndex: number;
  brandedStoreCoopAttribute?: string | null;
}

export const CAT_NAV_CAROUSEL = 'CatNavCarousel';

const CategoryNavCarousel = ({
  title,
  items,
  trackPromos,
  pageType,
  sliceIndex,
  brandedStoreCoopAttribute,
}: CategoryNavCarouselProps) => {
  const [slidesOffsetBefore, setSlidesOffsetBefore] = useState<null | number>(
    null
  );

  const XL_BREAKPOINT = 1260;
  const XXL_BREAKPOINT = 1616;

  const calculateSlidesOffsetBefore = (width: number) => {
    if (width < XL_BREAKPOINT) return 0;
    if (width >= XL_BREAKPOINT && width < XXL_BREAKPOINT)
      return (width - 1264) / 2;
    return (width - 1360) / 2;
  };

  useEffect(() => {
    const updateWidthAndOffset = () => {
      const currentWidth = window.innerWidth;

      setSlidesOffsetBefore(calculateSlidesOffsetBefore(currentWidth));
    };

    updateWidthAndOffset();
    const handleResizeWindow = throttle(updateWidthAndOffset, 100);

    window.addEventListener('resize', handleResizeWindow);
    return () => {
      window.removeEventListener('resize', handleResizeWindow);
    };
  }, []);

  const isDragging = useRef(false);

  if (!items) return null;

  const slides = items?.map((item: CategoryNavCarouselItem, index: number) => {
    const promoDetails = {
      id: item?.heading,
      name: `${item?.heading}-${pageType}-${CAT_NAV_CAROUSEL}`,
      type: 'link',
      url: item?.url,
      position: `${pageType}-${sliceIndex}-${CAT_NAV_CAROUSEL}${index}`,
    };

    const handleClick = (e: React.MouseEvent<HTMLElement>) => {
      if (isDragging.current) {
        e?.preventDefault();
        return;
      }

      eePromoClickHandler(promoDetails);

      trackPromotionEvent({
        eventName: PromotionEventName.selectPromotion,
        page: {
          path: window.location.pathname,
          page_type: determinePageType(),
        },
        interaction: {
          coop_brand: brandedStoreCoopAttribute || null,
          interaction_location: InteractionLocation.CatNavCarousel,
        },
        ecommerce: {
          creative_name: `link,${item?.url}`,
          creative_slot: buildCreativeSlotName({
            sliceIndex,
            interactionLocation: InteractionLocation.CatNavCarousel,
            itemIndex: index,
          }),
          promotion_id: item?.heading || '',
          promotion_name: item?.heading || '',
          isVideo: false,
        },
      });
    };

    return (
      <SwiperSlide
        key={`category-nav-carousel-${item?.heading}-${item?.url}`}
        className={styles.swiperSlide}
      >
        <CategoryNavItem
          item={item}
          trackPromos={trackPromos}
          pageType={pageType}
          sliceIndex={sliceIndex}
          index={index}
          brandedStoreCoopAttribute={brandedStoreCoopAttribute}
          handleClick={handleClick}
        />
      </SwiperSlide>
    );
  });

  return (
    <div className={styles.container}>
      {title && (
        <div className={styles.titleContainer}>
          <Heading semanticLevel="h2" styleLevel="2">
            {title}
          </Heading>
        </div>
      )}
      {slidesOffsetBefore !== null && (
        <Swiper
          modules={[FreeMode, Mousewheel]}
          className={styles.swiper}
          slidesPerView={'auto'}
          initialSlide={0}
          spaceBetween={12}
          slidesOffsetBefore={slidesOffsetBefore}
          mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
          freeMode={{ enabled: true }}
          onTouchStart={() => {
            isDragging.current = false;
          }}
          onTouchMove={() => {
            isDragging.current = true;
          }}
        >
          {slides}
        </Swiper>
      )}
    </div>
  );
};

export default CategoryNavCarousel;
